/**
 * CloudBackupNudge — gentle "save a copy to the cloud" prompt while travelling.
 * Surfaces with an active trip when there's new content since the last backup,
 * at most once per day. Two moments: app open and day-close (wayfare-day-closed).
 * Tapping "Save to cloud" opens the normal cloud-share flow.
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import autoBackupManager from '../utils/autoBackupManager';
import CloudSaveModal from './CloudSaveModal.jsx';
import theme from '../styles/theme';

export default function CloudBackupNudge({ hasActiveTrip }) {
  const { t } = useTranslation('common');
  const [show, setShow] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const maybeShow = useCallback(async () => {
    if (!hasActiveTrip) return;
    const ok = await autoBackupManager.shouldNudgeCloud().catch(() => false);
    if (ok) {
      // Start the 24h cooldown the moment we surface, so it never nags twice a day.
      autoBackupManager.snoozeCloudNudge();
      setShow(true);
    }
  }, [hasActiveTrip]);

  useEffect(() => { maybeShow(); }, [maybeShow]);

  useEffect(() => {
    const onDayClosed = () => maybeShow();
    window.addEventListener('wayfare-day-closed', onDayClosed);
    return () => window.removeEventListener('wayfare-day-closed', onDayClosed);
  }, [maybeShow]);

  if (showModal) {
    return <CloudSaveModal onClose={() => { setShowModal(false); setShow(false); }} />;
  }
  if (!show) return null;

  return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={S.title}>☁️ {t('cloudNudge.title')}</div>
        <div style={S.body}>{t('cloudNudge.body')}</div>
        <button type="button" style={S.upload} onClick={() => setShowModal(true)}>
          {t('cloudNudge.upload')}
        </button>
        <div style={S.row}>
          <button type="button" style={S.later} onClick={() => setShow(false)}>
            {t('cloudNudge.later')}
          </button>
          <button type="button" style={S.never}
            onClick={() => { autoBackupManager.disableCloudNudge(); setShow(false); }}>
            {t('cloudNudge.never')}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  wrap: { position: 'fixed', left: 0, right: 0, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 84px)', display: 'flex', justifyContent: 'center', padding: '0 16px', zIndex: 1240, pointerEvents: 'none' },
  card: { pointerEvents: 'auto', maxWidth: 420, width: '100%', background: theme.cream, border: `1px solid ${theme.bluePale}`, borderLeft: `3px solid ${theme.blue}`, borderRadius: 12, boxShadow: '0 6px 24px rgba(45,90,110,0.18)', padding: '14px 16px', animation: 'wf-whisper-in 360ms ease-out' },
  title: { fontSize: 15, fontWeight: 800, color: theme.blueDeep, marginBottom: 4 },
  body: { fontSize: 13, color: theme.brown, lineHeight: 1.4, marginBottom: 12 },
  upload: { width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: theme.terracotta, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 8 },
  row: { display: 'flex', gap: 10 },
  later: { flex: 1, padding: '9px', borderRadius: 10, border: `1.5px solid ${theme.sand}`, background: 'transparent', color: theme.brown, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  never: { flex: 1, padding: '9px', borderRadius: 10, border: 'none', background: 'transparent', color: theme.sand, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
